import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Search, Sparkles } from 'lucide-react'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import BusinessSelect from '@/components/ui/BusinessSelect'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import MemberSelect from '@/components/ui/MemberSelect'
import ReportTemplateSelect from '@/components/ui/ReportTemplateSelect'
import StatusSelect from '@/components/ui/StatusSelect'
import DateRangePicker from '@/components/ui/DateRangePicker'
import {
  getInitialDateRange,
  formatDateForAPI,
  loadDateRangeFromStorage,
  parseDateFromStorage
} from '@/components/ui/DateRangePicker/utils'
import { fetchBusinesses } from '@/store/businessSlice'
import { TaskReportService } from '@/api/generated/services/TaskReportService'
import type { TaskWithReportResponse } from '@/api/generated/models/TaskWithReportResponse'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import type { ColDef } from 'ag-grid-community'
import ReportDetailPanel from './components/ReportDetailPanel'
import BatchAiAnalysisModal from './components/BatchAiAnalysisModal'
import AiJobStatusBanner from './components/AiJobStatusBanner'
import { useCanUseAI } from '@/hooks/useCanUseAI'
import { useTaskEditFromReport } from '@/hooks/useTaskEditFromReport'
import { usePersistedBusinessFilter } from '@/hooks/usePersistedBusinessFilter'

const ReportManagementPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const currentWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const { businesses, isLoading: businessLoading, error: businessError } = useAppSelector(state => state.business)

  // 워크스페이스 AI 설정 + 내 역할 기반 AI UI 노출 여부
  const canUseAI = useCanUseAI()

  // 일괄 AI 분석 모달 + 작업 배너 갱신 토큰
  const [isBatchAiModalOpen, setIsBatchAiModalOpen] = useState(false)
  const [jobsRefreshToken, setJobsRefreshToken] = useState(0)

  // UI state for title area controls
  const [searchValue, setSearchValue] = useState('')

  // Report template data
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isReportTemplatesLoading, setIsReportTemplatesLoading] = useState(false)
  const [reportTemplatesError, setReportTemplatesError] = useState<string | null>(null)

  // Filter state
  const [selectedReportTemplateKey, setSelectedReportTemplateKey] = useState<string>('')
  const [selectedReportTemplateName, setSelectedReportTemplateName] = useState<string>('')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('')
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<string>('')
  const {
    businessId: selectedBusinessId,
    businessName: selectedBusinessName,
    setBusiness: saveSelectedBusiness,
  } = usePersistedBusinessFilter()
  const [selectedMemberId, setSelectedMemberId] = useState<string>('')
  const [selectedMemberName, setSelectedMemberName] = useState<string>('')
  const [, setSelectedMemberEmail] = useState<string>('')
  const [selectedTaskStatus, setSelectedTaskStatus] = useState<string>('')
  const [selectedReportStatus, setSelectedReportStatus] = useState<string>('')

  // Date range state - initialize from localStorage
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const [initialStart] = getInitialDateRange()
    return initialStart
  })
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const [, initialEnd] = getInitialDateRange()
    return initialEnd
  })

  // State to trigger re-render when localStorage changes
  const [storageUpdateTrigger, setStorageUpdateTrigger] = useState(0)

  // Get display dates from localStorage (for DateRangePicker display)
  const displayDates = useMemo((): [Date | null, Date | null] => {
    try {
      const stored = loadDateRangeFromStorage()
      return [
        parseDateFromStorage(stored.start_date),
        parseDateFromStorage(stored.end_date)
      ]
    } catch {
      const [initialStart, initialEnd] = getInitialDateRange()
      return [initialStart, initialEnd]
    }
  }, [storageUpdateTrigger])

  // Report data state
  const [reportData, setReportData] = useState<TaskWithReportResponse[]>([])
  const [filteredReportData, setFilteredReportData] = useState<TaskWithReportResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Report detail panel state
  const [isReportDetailPanelOpen, setIsReportDetailPanelOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState<TaskWithReportResponse | null>(null)

  // Load businesses, report templates, and report data on component mount
  useEffect(() => {
    if (currentWorkspace?.id) {
      dispatch(fetchBusinesses({ workspaceId: currentWorkspace.id, page: 1, perPage: 100 }))
      loadReportTemplates()
      fetchReportData()
    }
  }, [currentWorkspace?.id, dispatch])

  // Reload report templates when business selection changes
  useEffect(() => {
    if (currentWorkspace?.id) {
      loadReportTemplates()
    }
  }, [selectedBusinessId])

  // Load report templates
  const loadReportTemplates = async () => {
    if (!currentWorkspace?.id) return

    try {
      setIsReportTemplatesLoading(true)
      setReportTemplatesError(null)

      const response = await TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
        workspaceId: currentWorkspace.id,
        businessId: selectedBusinessId || undefined,
      })

      console.log('Report templates API response:', response)

      if (response) {
        // API now returns array directly
        setReportTemplates(response)
      } else {
        setReportTemplates([])
      }
    } catch (err) {
      console.error('Failed to load report templates:', err)
      setReportTemplatesError('보고서 유형 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsReportTemplatesLoading(false)
    }
  }

  // Fetch report data with filters
  const fetchReportData = useCallback(async () => {
    if (!currentWorkspace?.id) return

    try {
      setIsLoading(true)
      setError(null)

      // Build API parameters object
      const apiParams: {
        workspaceId: string
        businessId?: string
        departmentId?: string
        assigneeId?: string
        reportTemplateKey?: string
        startDate?: string
        endDate?: string
      } = {
        workspaceId: currentWorkspace.id,
        businessId: selectedBusinessId || undefined,
        departmentId: selectedDepartmentId || undefined,
        assigneeId: selectedMemberId || undefined,
        reportTemplateKey: selectedReportTemplateKey || undefined,
      }

      // Add date parameters if both dates are set
      if (startDate && endDate) {
        apiParams.startDate = formatDateForAPI(startDate)
        apiParams.endDate = formatDateForAPI(endDate)
      }

      const response = await TaskReportService.appsTaskViewsGetWorkspaceAllTaskReports(apiParams)

      setReportData(response)
      setFilteredReportData(response)
    } catch (err) {
      console.error('Failed to fetch report data:', err)
      setError('보고서 데이터를 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [currentWorkspace?.id, selectedBusinessId, selectedDepartmentId, selectedMemberId, selectedReportTemplateKey, startDate, endDate])

  // Refresh data when filters change
  useEffect(() => {
    fetchReportData()
  }, [fetchReportData])

  // Status formatters
  const formatTaskStatus = useCallback((status: string) => {
    switch (status) {
      case 'incomplete':
        return '미완료'
      case 'completed':
        return '완료'
      default:
        return status
    }
  }, [])

  const formatTaskPerformanceStatus = useCallback((status: string) => {
    switch (status) {
      case 'incomplete':
        return '미완료'
      case 'completed':
        return '완료'
      default:
        return status
    }
  }, [])

  const formatReportStatus = useCallback((report: any) => {
    if (!report) return '미완료'
    switch (report.status) {
      case 'incomplete':
        return '미완료'
      case 'completed':
        return '완료'
      default:
        return report.status
    }
  }, [])

  const formatDate = useCallback((dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR')
  }, [])

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '보고서유형',
      field: 'report_template.name',
      width: 200,
      minWidth: 200,
      filter: false,
      valueGetter: (params) => params.data?.report_template?.name || '-',
    },
    {
      headerName: '사업명',
      field: 'task.business_name',
      width: 150,
      minWidth: 150,
      filter: false,
      valueGetter: (params) => params.data?.task?.business_name || '-',
    },
    {
      headerName: '전략과제명',
      field: 'task.task_group_name',
      width: 250,
      minWidth: 250,
      filter: false,
      valueGetter: (params) => params.data?.task?.task_group_name || '-',
    },
    {
      headerName: '실행과제명',
      field: 'task.task_subgroup_name',
      width: 250,
      minWidth: 250,
      filter: false,
      valueGetter: (params) => params.data?.task?.task_subgroup_name || '-',
    },
    {
      headerName: '세부실행과제명',
      field: 'task.name',
      width: 300,
      minWidth: 300,
      filter: false,
      valueGetter: (params) => params.data?.task?.name || '-',
    },
    {
      headerName: '세부실행과제상태',
      field: 'task.status',
      width: 100,
      minWidth: 100,
      filter: false,
      cellRenderer: (params: any) => {
        const status = formatTaskStatus(params.value)
        const color = params.value === 'completed' ? 'success' : 'warning'
        return (
          <Tag color={color} size="small">
            {status}
          </Tag>
        )
      },
    },
    {
      headerName: '성과상태',
      field: 'task.performance_status',
      width: 100,
      minWidth: 100,
      filter: false,
      cellRenderer: (params: any) => {
        const status = formatTaskPerformanceStatus(params.value)
        const color = params.value === 'completed' ? 'success' : 'warning'
        return (
          <Tag color={color} size="small">
            {status}
          </Tag>
        )
      },
    },
    {
      headerName: '보고서상태',
      field: 'report.status',
      width: 100,
      minWidth: 100,
      filter: false,
      cellRenderer: (params: any) => {
        const status = formatReportStatus(params.data?.report)
        const color = status === '완료' ? 'success' : 'warning'
        return (
          <Tag color={color} size="small">
            {status}
          </Tag>
        )
      },
    },
    {
      headerName: '평가등급',
      field: 'report.grade',
      width: 90,
      minWidth: 90,
      filter: false,
      cellRenderer: (params: any) => {
        const grade = params.data?.report?.grade
        if (!grade) return '-'

        const getGradeColor = (g: string) => {
          switch (g) {
            case 'S': return 'secondary'
            case 'A': return 'success'
            case 'B': return 'info'
            case 'C': return 'warning'
            case 'D': return 'error'
            default: return 'default'
          }
        }

        return (
          <Tag color={getGradeColor(grade)} size="small">
            {grade}
          </Tag>
        )
      },
    },
    {
      headerName: '부서명',
      field: 'task.department_name',
      width: 150,
      minWidth: 150,
      filter: false,
      valueGetter: (params) => params.data?.task?.department_name || '-',
    },
    {
      headerName: '담당자명',
      field: 'task.assignee.name',
      width: 100,
      minWidth: 100,
      filter: false,
      valueGetter: (params) => params.data?.task?.assignee?.name || '-',
    },
    {
      headerName: '관리자명',
      field: 'task.manager.name',
      width: 100,
      minWidth: 100,
      filter: false,
      valueGetter: (params) => params.data?.task?.manager?.name || '-',
    },
    {
      headerName: '검토자명',
      field: 'task.reviewer.name',
      width: 100,
      minWidth: 100,
      filter: false,
      valueGetter: (params) => params.data?.task?.reviewer?.name || '-',
    },
    {
      headerName: '시작일',
      field: 'task.start_at',
      width: 120,
      minWidth: 120,
      filter: false,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: '종료일',
      field: 'task.end_at',
      width: 120,
      minWidth: 120,
      filter: false,
      valueFormatter: (params) => formatDate(params.value),
    },
  ], [formatTaskStatus, formatReportStatus, formatDate])

  // Filter handlers
  const handleReportTemplateSelect = useCallback((templateKey: string, templateName: string) => {
    setSelectedReportTemplateKey(templateKey)
    setSelectedReportTemplateName(templateName)
  }, [])

  const handleDepartmentSelect = useCallback((departmentId: string, departmentName: string) => {
    setSelectedDepartmentId(departmentId)
    setSelectedDepartmentName(departmentName)

    // Reset member selection when department changes
    setSelectedMemberId('')
    setSelectedMemberName('')
    setSelectedMemberEmail('')
  }, [])

  const handleBusinessSelect = useCallback((businessId: string, businessName: string) => {
    saveSelectedBusiness(businessId, businessName)
  }, [saveSelectedBusiness])

  const handleMemberSelect = useCallback((memberId: string, memberName: string, memberEmail: string) => {
    setSelectedMemberId(memberId)
    setSelectedMemberName(memberName)
    setSelectedMemberEmail(memberEmail)
  }, [])

  const handleTaskStatusSelect = useCallback((status: string) => {
    setSelectedTaskStatus(status)
  }, [])

  const handleReportStatusSelect = useCallback((status: string) => {
    setSelectedReportStatus(status)
  }, [])

  // Status options
  const taskStatusOptions = [
    { value: '', label: '전체' },
    { value: 'incomplete', label: '미완료' },
    { value: 'completed', label: '완료' }
  ]

  const reportStatusOptions = [
    { value: '', label: '전체' },
    { value: 'incomplete', label: '미완료' },
    { value: 'completed', label: '완료' }
  ]

  // Handle date range change from DateRangePicker
  const handleDateRangeChange = useCallback((newStartDate: Date | null, newEndDate: Date | null) => {
    // Only update if both dates are provided (indicating a complete range selection)
    if (newStartDate && newEndDate) {
      setStartDate(newStartDate)
      setEndDate(newEndDate)
      // Trigger display dates update (localStorage was updated by DateRangePicker)
      setStorageUpdateTrigger(prev => prev + 1)
    }
  }, [])

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSelectedReportTemplateKey('')
    setSelectedReportTemplateName('')
    setSelectedDepartmentId('')
    setSelectedDepartmentName('')
    saveSelectedBusiness('', '')
    setSelectedMemberId('')
    setSelectedMemberName('')
    setSelectedMemberEmail('')
    setSelectedTaskStatus('')
    setSelectedReportStatus('')

    // Reset date range to initial values
    const [initialStart, initialEnd] = getInitialDateRange()
    setStartDate(initialStart)
    setEndDate(initialEnd)
    setStorageUpdateTrigger(prev => prev + 1)

    // Reset search
    setSearchValue('')
    setFilteredReportData(reportData)
  }, [reportData, saveSelectedBusiness])

  const handleSearch = useCallback(() => {
    if (!searchValue.trim()) {
      // Empty search: show all data
      setFilteredReportData(reportData)
      return
    }

    // Filter by task name (case-insensitive, partial match)
    const searchTerm = searchValue.trim().toLowerCase()
    const filtered = reportData.filter(item => {
      const taskName = item.task?.name?.toLowerCase() || ''
      return taskName.includes(searchTerm)
    })

    setFilteredReportData(filtered)
  }, [searchValue, reportData])

  // Auto-filter when reportData, searchValue, or status filters change
  useEffect(() => {
    let filtered = reportData

    // Apply search filter
    if (searchValue.trim()) {
      const searchTerm = searchValue.trim().toLowerCase()
      filtered = filtered.filter(item => {
        const taskName = item.task?.name?.toLowerCase() || ''
        return taskName.includes(searchTerm)
      })
    }

    // Apply task status filter
    if (selectedTaskStatus) {
      filtered = filtered.filter(item => item.task?.status === selectedTaskStatus)
    }

    // Apply report status filter
    if (selectedReportStatus) {
      filtered = filtered.filter(item => {
        const reportStatus = item.report?.status || 'incomplete'
        return reportStatus === selectedReportStatus
      })
    }

    setFilteredReportData(filtered)
  }, [reportData, searchValue, selectedTaskStatus, selectedReportStatus])

  // const handleMoreOptions = useCallback(() => {
  //   console.warn('더보기 기능 미구현')
  // }, [])

  // Report detail panel handlers
  const handleRowClick = useCallback(async (params: any) => {
    const report = params.data as TaskWithReportResponse
    console.log('Row clicked:', report)

    // Try to get report template key from row data first, then fall back to selected filter
    const reportTemplateKey = report.report_template?.key || selectedReportTemplateKey
    const taskId = report.task?.id
    const taskName = report.task?.name

    console.log('Report template Key:', reportTemplateKey, 'Task ID:', taskId, 'Task name:', taskName)

    if (report && reportTemplateKey && taskId) {
      setSelectedReport(report)

      // Reload report templates with the selected report's business_id
      if (report.task?.business_id && currentWorkspace?.id) {
        try {
          setIsReportTemplatesLoading(true)
          setReportTemplatesError(null)

          const response = await TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
            workspaceId: currentWorkspace.id,
            businessId: report.task.business_id,
          })

          console.log('Report templates loaded for business:', report.task.business_id, response)

          if (response) {
            setReportTemplates(response)
          } else {
            setReportTemplates([])
          }
        } catch (err) {
          console.error('Failed to load report templates for detail panel:', err)
          setReportTemplatesError('보고서 유형 목록을 불러오는데 실패했습니다.')
        } finally {
          setIsReportTemplatesLoading(false)
        }
      }

      setIsReportDetailPanelOpen(true)
      console.log('Opening report detail panel')
    } else {
      console.warn('Cannot open panel - missing required data:', {
        report: !!report,
        reportTemplateKey,
        taskId,
        taskName,
      })
    }
  }, [selectedReportTemplateKey, currentWorkspace?.id])

  const handleReportDetailPanelClose = useCallback(() => {
    setIsReportDetailPanelOpen(false)
    setSelectedReport(null)
  }, [])

  // Handle report type change in ReportDetailPanel
  const handleReportTypeChange = useCallback(async (reportKey: string, reportName: string) => {
    if (!selectedReport?.task?.id || !reportKey) {
      console.warn('Cannot change report type - missing task ID or report key')
      return
    }

    try {
      // Update the selectedReport with new report template
      setSelectedReport(prevReport => {
        if (!prevReport) return prevReport
        return {
          ...prevReport,
          report_template: {
            key: reportKey,
            name: reportName
          }
        }
      })

      // Force reload of report preview and data with new template
      // The ReportDetailPanel will handle the API call using the new reportKey
      console.log('Report type changed to:', reportName, 'with key:', reportKey)

    } catch (error) {
      console.error('Failed to change report type:', error)
    }
  }, [selectedReport?.task?.id])

  // 보고서 패널의 "세부실행과제 수정" → 보고서 패널을 닫고 수정 패널을 연다. 저장 후 목록 재조회
  const { openTaskEdit, taskEditPanel } = useTaskEditFromReport({
    onBeforeOpen: handleReportDetailPanelClose,
    onSuccess: fetchReportData,
  })


  return (
    <div className="p-6 h-full flex flex-col">
      {/* Filter & Action Bar - Single Row */}
      <div
        className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg"
        style={{
          backgroundColor: designTokens.colors.gray[0],
        }}
      >
        {/* Date Range Selection */}
        <div className="w-[230px] flex-shrink-0">
          <DateRangePicker
            startDate={displayDates[0]}
            endDate={displayDates[1]}
            onDateRangeChange={handleDateRangeChange}
            timePeriod="period"
            onTimePeriodChange={() => {}}
            triggerMode="icon"
            showDateText={true}
            workspaceId={currentWorkspace?.id}
          />
        </div>

        {/* Report Template Selection */}
        <div className="w-[180px] flex-shrink-0">
          <ReportTemplateSelect
            workspaceId={currentWorkspace?.id || ''}
            templates={reportTemplates}
            isLoading={isReportTemplatesLoading}
            templatesError={reportTemplatesError}
            selectedReportTemplateId={selectedReportTemplateKey}
            selectedReportTemplateName={selectedReportTemplateName || '[전체] 보고서 선택'}
            onSelect={handleReportTemplateSelect}
            placeholder="[전체] 보고서 선택"
            showAllOption={true}
          />
        </div>

        {/* Business Selection */}
        <div className="w-[180px] flex-shrink-0">
          <BusinessSelect
            workspaceId={currentWorkspace?.id || ''}
            businesses={businesses}
            isLoading={businessLoading}
            businessesError={businessError}
            selectedBusinessId={selectedBusinessId}
            selectedBusinessName={selectedBusinessName || '[전체] 사업 선택'}
            onSelect={handleBusinessSelect}
            placeholder="[전체] 사업 선택"
            showAllOption={true}
          />
        </div>

        {/* Department Selection */}
        <div className="w-[180px] flex-shrink-0">
          <DepartmentSelect
            workspaceId={currentWorkspace?.id || ''}
            selectedDepartmentId={selectedDepartmentId}
            selectedDepartmentName={selectedDepartmentName || '[전체] 부서 선택'}
            onSelect={handleDepartmentSelect}
            placeholder="[전체] 부서 선택"
            showAllOption={true}
          />
        </div>

        {/* Member Selection */}
        <div className="w-[180px] flex-shrink-0">
          <MemberSelect
            workspaceId={currentWorkspace?.id || ''}
            selectedMemberId={selectedMemberId}
            selectedMemberName={selectedMemberName || '[전체] 담당자 선택'}
            onSelect={handleMemberSelect}
            placeholder="[전체] 담당자 선택"
            label="담당자"
            showAllOption={true}
          />
        </div>

        {/* Task Status Selection */}
        <div className="w-[180px] flex-shrink-0">
          <StatusSelect
            options={taskStatusOptions}
            selectedValue={selectedTaskStatus}
            onSelect={handleTaskStatusSelect}
            placeholder="[전체] 과제상태"
          />
        </div>

        {/* Report Status Selection */}
        <div className="w-[180px] flex-shrink-0">
          <StatusSelect
            options={reportStatusOptions}
            selectedValue={selectedReportStatus}
            onSelect={handleReportStatusSelect}
            placeholder="[전체] 보고서상태"
          />
        </div>

        {/* Clear Filters Button - conditional */}
        {((selectedReportTemplateKey && selectedReportTemplateName !== '전체') ||
          (selectedDepartmentId && selectedDepartmentName !== '전체') ||
          (selectedBusinessId && selectedBusinessName !== '전체') ||
          (selectedMemberId && selectedMemberName !== '전체') ||
          selectedTaskStatus ||
          selectedReportStatus) && (
          <Button
            size="medium"
            color="white"
            onClick={clearAllFilters}
            className="h-10 px-[10px] flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
          >
            필터 초기화
          </Button>
        )}

        {/* Search Input */}
        <div className="relative w-[280px] flex-shrink-0">
          <div
            className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
            style={{
              backgroundColor: designTokens.colors.white,
              borderColor: designTokens.colors.gray[1],
            }}
          >
            <Search
              className="w-5 h-5"
              style={{ color: designTokens.colors.gray[3] }}
            />
            <input
              type="text"
              placeholder="세부실행과제명으로 검색해주세요."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex-1 bg-transparent outline-none min-w-0"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                color: searchValue ? designTokens.colors.gray[6] : designTokens.colors.gray[3],
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </div>
        </div>

        {/* Search Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleSearch}
          className="h-10 px-[10px] flex-shrink-0 whitespace-nowrap"
        >
          검색
        </Button>

        {/* 일괄 AI 분석 Button */}
        {canUseAI && (
          <Button
            color="dark"
            size="medium"
            onClick={() => setIsBatchAiModalOpen(true)}
            className="h-10 px-[10px] flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap"
          >
            <Sparkles className="w-4 h-4" strokeWidth={2} />
            일괄 AI 분석
          </Button>
        )}
      </div>

      {/* AI 분석 작업 상태 배너 (진행 중 작업 + 워커 미실행 경고) */}
      {canUseAI && currentWorkspace?.id && (
        <AiJobStatusBanner
          workspaceId={currentWorkspace.id}
          refreshToken={jobsRefreshToken}
        />
      )}

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 mt-6">
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400 m-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <DataGrid
            columnDefs={columnDefs}
            rowData={filteredReportData}
            loading={isLoading}
            className="ag-theme-alpine"
            headerHeight={40}
            rowHeight={42}
            pagination={true}
            paginationPageSize={20}
            paginationPageSizeSelector={[10, 20, 50, 100]}
            height="calc(100vh - 260px)"
            onRowClicked={handleRowClick}
            // 범위 선택 기능 활성화
            enableRangeSelection={true}
            enableCellCopy={true}
            suppressRowClickSelection={true}
            rangeCopyFormatter={(colId, value) => {
              // 평가등급 포맷팅
              if (colId === 'report.grade') {
                return value || '-'
              }
              // 과제상태 포맷팅
              if (colId === 'task.status') {
                return formatTaskStatus(value)
              }
              // 보고서상태 포맷팅 (report.status는 'incomplete' | 'completed' 등)
              if (colId === 'report.status') {
                if (!value) return '미완료'
                if (value === 'incomplete') return '미완료'
                if (value === 'completed') return '완료'
                return value
              }
              // 날짜 포맷팅
              if (colId === 'task.start_at' || colId === 'task.end_at') {
                return formatDate(value)
              }
              // 기본값 처리
              if (value === null || value === undefined) return '-'
              return value.toString()
            }}
          />
        </div>
      </div>

      {/* Report Detail Panel — id/templateKey만 전달, 패널이 자체 fetch */}
      <ReportDetailPanel
        isOpen={isReportDetailPanelOpen}
        onClose={handleReportDetailPanelClose}
        taskId={selectedReport?.task?.id ?? null}
        reportTemplateKey={selectedReport?.report_template?.key ?? selectedReportTemplateKey ?? null}
        reportTemplates={reportTemplates}
        isReportTemplatesLoading={isReportTemplatesLoading}
        reportTemplatesError={reportTemplatesError}
        workspaceId={currentWorkspace?.id || ''}
        onReportTypeChange={handleReportTypeChange}
        onEditTask={openTaskEdit}
        onReportUpdate={fetchReportData}
      />

      {/* 일괄 AI 분석 모달 */}
      {currentWorkspace?.id && (
        <BatchAiAnalysisModal
          isOpen={isBatchAiModalOpen}
          onClose={() => setIsBatchAiModalOpen(false)}
          workspaceId={currentWorkspace.id}
          defaultBusinessId={selectedBusinessId}
          defaultBusinessName={selectedBusinessName}
          defaultDepartmentId={selectedDepartmentId}
          defaultDepartmentName={selectedDepartmentName}
          defaultReportTemplateKey={selectedReportTemplateKey}
          defaultReportTemplateName={selectedReportTemplateName}
          onStarted={() => setJobsRefreshToken(prev => prev + 1)}
        />
      )}

      {/* 세부실행과제 수정 패널 — 보고서 패널의 "세부실행과제 수정" 클릭 시 */}
      {taskEditPanel}
    </div>
  )
}

export default ReportManagementPage